import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Checkbox,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { getKizValidationError, getShortKiz } from "../../../utils/kiz";
import DashboardLayout from "../../../components/layout/DashboardLayout";
import { setMarket } from "../../../redux/slices/uiSlice";
import { fetchAllPostings } from "../../../api/postings";
import {
  computeReturnStatus,
  saveKizForItem,
  setItemReturnStatus,
  updatePostingReturnStatus,
} from "../../../api/kiz";
import {
  fetchReturnQueue,
  markKizReturned,
  setReturnScanDestroy,
  upsertReturnScan,
} from "../../../api/returns";

const MARKETS = [
  { key: "ozon", label: "Ozon" },
  { key: "wb", label: "WB" },
  { key: "ym", label: "Yandex Market" },
];

const RETURN_LABELS = {
  returned_all: { label: "Возвращён", color: "success" },
  need_return: { label: "Надо вернуть", color: "warning" },
};

const RETURN_PREFIX = "Вернул в продажу | ";
const TWO_MONTHS_MS = 1000 * 60 * 60 * 24 * 60;

function normalizeKiz(code) {
  const clean = String(code || "").trim();
  if (!clean) return "";
  if (clean.startsWith(RETURN_PREFIX)) {
    return clean.slice(RETURN_PREFIX.length).trim();
  }
  return clean;
}

function getItemCodes(item) {
  const base = normalizeKiz(item.kiz_code);
  const additional = Array.isArray(item.kiz_additional_codes)
    ? item.kiz_additional_codes.map(normalizeKiz)
    : [];
  return [base, ...additional].filter(Boolean);
}

export default function ReturnsPage() {
  const market = useSelector((s) => s.ui.market);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [queue, setQueue] = useState([]);
  const [postings, setPostings] = useState([]);
  const [scanValue, setScanValue] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [destroyUpdatingId, setDestroyUpdatingId] = useState(null);

  const marketMap = useMemo(
    () =>
      MARKETS.reduce(
        (acc, m) => {
          acc[m.key] = m.label;
          return acc;
        },
        {},
      ),
    [],
  );

  useEffect(() => {
    let active = true;
    async function loadData() {
      setLoading(true);
      try {
        const since = new Date(Date.now() - TWO_MONTHS_MS).toISOString();
        const [queueRows, ...marketResults] = await Promise.all([
          fetchReturnQueue(),
          ...MARKETS.map((m) =>
            fetchAllPostings({ market: m.key, state: "cancelled", since }),
          ),
        ]);
        if (!active) return;
        setQueue(queueRows);
        setPostings(marketResults.flat());
      } catch (error) {
        console.error("Не удалось загрузить данные возвратов", error);
        enqueueSnackbar("Не удалось загрузить данные возвратов", {
          variant: "error",
        });
      } finally {
        if (active) setLoading(false);
      }
    }

    loadData();

    return () => {
      active = false;
    };
  }, [enqueueSnackbar, reloadToken]);

  const rows = useMemo(
    () => {
      function findMatch(code) {
        const clean = normalizeKiz(code);
        if (!clean) return null;

        for (const posting of postings) {
          const items = Array.isArray(posting.items) ? posting.items : [];
          for (const item of items) {
            if (getItemCodes(item).includes(clean)) {
              return { posting, item, code: clean };
            }
          }
        }

        return null;
      }

      return queue.map((entry) => {
        const match = findMatch(entry.code_raw);
        return {
          id: entry.documentId || entry.id,
          queueDocumentId: entry.documentId || entry.id,
          kizCode: entry.code_raw,
          destroyKiz: !!entry.destroy_kiz,
          postingId: match?.posting?.id ?? null,
          postingDocumentId: match?.posting?.documentId ?? null,
          postingNumber: match?.posting?.posting_number ?? null,
          market: match?.posting?.market ?? null,
          inProcessAt: match?.posting?.in_process_at ?? null,
          returnStatus: match?.posting?.return_status ?? null,
          itemDocumentId: match?.item?.documentId ?? null,
          itemReturned: !!match?.item?.kiz_returned,
        };
      });
    },
    [queue, postings],
  );

  async function handleAddScan() {
    const clean = normalizeKiz(scanValue);
    if (!clean) return;
    const validationError = getKizValidationError(clean);
    if (validationError) {
      enqueueSnackbar(validationError, { variant: "error" });
      return;
    }

    try {
      await upsertReturnScan(clean);
      setReloadToken((x) => x + 1);
      setScanValue("");
    } catch (error) {
      console.error("Не удалось сохранить КИЗ", error);
      enqueueSnackbar(error.message || "Не удалось сохранить КИЗ", {
        variant: "error",
      });
    }
  }

  async function handleReturn(row) {
    if (!row.queueDocumentId) return;
    setSavingId(row.id);

    try {
      if (row.itemDocumentId && row.postingDocumentId) {
        const targetPosting = postings.find((p) => p.id === row.postingId);
        const postingItems = Array.isArray(targetPosting?.items)
          ? targetPosting.items
          : [];
        const updatedItems = postingItems.map((item) => {
          if (item.documentId !== row.itemDocumentId) return item;
          const cleanCode = normalizeKiz(item.kiz_code);
          const nextCode = cleanCode
            ? `${RETURN_PREFIX}${cleanCode}`
            : item.kiz_code;
          return { ...item, kiz_returned: true, kiz_code: nextCode };
        });
        const nextStatus = targetPosting
          ? computeReturnStatus(updatedItems)
          : row.returnStatus || "need_return";

        await setItemReturnStatus(row.itemDocumentId, true);
        const targetItem = postingItems.find(
          (item) => item.documentId === row.itemDocumentId,
        );
        const cleanCode = normalizeKiz(targetItem?.kiz_code);
        if (cleanCode) {
          const nextCode = `${RETURN_PREFIX}${cleanCode}`;
          await saveKizForItem(row.itemDocumentId, nextCode);
        }
        await updatePostingReturnStatus(row.postingDocumentId, nextStatus);

        setPostings((prev) =>
          prev.map((posting) => {
            if (posting.id !== row.postingId) return posting;
            return { ...posting, items: updatedItems, return_status: nextStatus };
          }),
        );
      }

      await markKizReturned(row.queueDocumentId);
      setQueue((prev) =>
        prev.filter(
          (entry) => (entry.documentId || entry.id) !== row.queueDocumentId,
        ),
      );

      enqueueSnackbar("КИЗ отмечен как возвращенный в оборот", {
        variant: "success",
      });
    } catch (error) {
      console.error("Не удалось вернуть КИЗ в оборот", error);
      enqueueSnackbar("Не удалось вернуть КИЗ в оборот", {
        variant: "error",
      });
    } finally {
      setSavingId(null);
    }
  }

  async function handleRemove(row) {
    if (!row.queueDocumentId) return;
    setSavingId(row.id);

    try {
      await markKizReturned(row.queueDocumentId);
      setQueue((prev) =>
        prev.filter(
          (entry) => (entry.documentId || entry.id) !== row.queueDocumentId,
        ),
      );
      enqueueSnackbar("КИЗ удалён из списка возвратов", {
        variant: "success",
      });
    } catch (error) {
      console.error("Не удалось удалить КИЗ из списка возвратов", error);
      enqueueSnackbar("Не удалось удалить КИЗ из списка возвратов", {
        variant: "error",
      });
    } finally {
      setSavingId(null);
    }
  }

  async function handleToggleDestroy(row) {
    if (!row.queueDocumentId) return;
    const nextValue = !row.destroyKiz;
    setDestroyUpdatingId(row.id);

    try {
      await setReturnScanDestroy(row.queueDocumentId, nextValue);
      setQueue((prev) =>
        prev.map((entry) => {
          const entryId = entry.documentId || entry.id;
          if (entryId !== row.queueDocumentId) return entry;
          return { ...entry, destroy_kiz: nextValue };
        }),
      );
    } catch (error) {
      console.error("Не удалось обновить отметку удаления КИЗ", error);
      enqueueSnackbar("Не удалось обновить отметку удаления КИЗ", {
        variant: "error",
      });
    } finally {
      setDestroyUpdatingId(null);
    }
  }

  return (
    <DashboardLayout
      market={market}
      onSelectMarket={(value) => dispatch(setMarket(value))}
    >
      <Stack spacing={2}>
        <Paper sx={{ p: 2 }} variant="outlined">
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            spacing={2}
          >
            <Box>
              <Typography variant="h6">Возвраты</Typography>
              <Typography variant="body2" color="text.secondary">
                Сканируйте КИЗ возвращенных товаров, чтобы найти заказ и
                подтвердить возврат в оборот.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              onClick={() => setReloadToken((x) => x + 1)}
              disabled={loading}
            >
              Обновить
            </Button>
          </Stack>
        </Paper>

        <Paper sx={{ p: 2 }} variant="outlined">
          <Stack spacing={2}>
            <TextField
              label="КИЗ возвращенного товара"
              placeholder="Отсканируйте или введите КИЗ"
              value={scanValue}
              onChange={(event) => setScanValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleAddScan();
                }
              }}
              autoFocus
              fullWidth
            />
            <Box>
              <Button
                variant="contained"
                onClick={handleAddScan}
                disabled={!scanValue.trim()}
              >
                Добавить
              </Button>
            </Box>
          </Stack>
        </Paper>

        <Paper sx={{ p: 2 }} variant="outlined">
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle1">Список возвратов</Typography>
            {loading && <CircularProgress size={18} />}
          </Stack>

          <TableContainer sx={{ maxHeight: 520 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center">Удаление КИЗ</TableCell>
                  <TableCell>КИЗ</TableCell>
                  <TableCell>Заказ</TableCell>
                  <TableCell>Маркетплейс</TableCell>
                  <TableCell>Дата заказа</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell align="right">Действие</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const returnMeta = row.returnStatus
                    ? RETURN_LABELS[row.returnStatus]
                    : null;
                  return (
                    <TableRow
                      key={row.id}
                      hover
                      sx={{
                        backgroundColor: row.destroyKiz
                          ? (theme) => alpha(theme.palette.error.main, 0.08)
                          : "inherit",
                        "&:hover": {
                          backgroundColor: row.destroyKiz
                            ? (theme) => alpha(theme.palette.error.main, 0.14)
                            : "inherit",
                        },
                      }}
                    >
                      <TableCell align="center">
                        <Checkbox
                          checked={row.destroyKiz}
                          onChange={() => handleToggleDestroy(row)}
                          color="error"
                          size="small"
                          disabled={destroyUpdatingId === row.id}
                          slotProps={{
                            input: { "aria-label": "Отметить удаление КИЗ" },
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <code style={{ fontSize: 12 }}>{row.kizCode}</code>
                          <Typography variant="caption" color="text.secondary">
                            {getShortKiz(row.kizCode) || "—"}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>{row.postingNumber || "—"}</TableCell>
                      <TableCell>
                        {row.market ? marketMap[row.market] : "—"}
                      </TableCell>
                      <TableCell>
                        {row.inProcessAt
                          ? new Date(row.inProcessAt).toLocaleString()
                          : "—"}
                      </TableCell>
                      <TableCell>
                        {returnMeta ? (
                          <Chip
                            size="small"
                            label={returnMeta.label}
                            color={returnMeta.color}
                          />
                        ) : (
                          <Chip size="small" label="—" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {row.itemReturned ? (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleRemove(row)}
                            disabled={savingId === row.id}
                          >
                            Удалить из списка
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleReturn(row)}
                            disabled={savingId === row.id}
                          >
                            Вернул в оборот
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}

                {rows.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      Нет отсканированных КИЗ
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Stack>
    </DashboardLayout>
  );
}
